import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'general'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxMFEhAGDpuUbFSH6pelJhOfYnq9C4CX0zqLnIl_9OR_yUsa4gUY8eay3zhnzFGG5JTtQ/exec';
    
    if (!GOOGLE_SCRIPT_URL || GOOGLE_SCRIPT_URL.includes('TU_URL_AQUI')) {
      alert('Por favor, configura la URL de Google Apps Script en el código.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        type: formData.type,
        source: 'Contact Form',
        timestamp: new Date().toISOString()
      };

      console.log('📤 Enviando datos a Google Sheets:', payload);

      let response;
      let success = false;

      try {
        response = await fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        console.log('📥 Respuesta recibida:', response.status, response.statusText);

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Resultado:', result);
          
          if (result.status === 'success') {
            success = true;
          } else {
            throw new Error(result.message || 'Error desconocido del servidor');
          }
        } else {
          throw new Error(`Error HTTP: ${response.status} ${response.statusText}`);
        }
      } catch (corsError) {
        console.warn('⚠️ Error CORS, intentando con no-cors:', corsError);
        
        try {
          await fetch(GOOGLE_SCRIPT_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
          });
          
          console.log('📤 Datos enviados con no-cors (no podemos verificar respuesta)');
          success = true;
        } catch (noCorsError) {
          console.error('❌ Error incluso con no-cors:', noCorsError);
          throw new Error('No se pudo enviar el formulario. Verifica la URL de Google Apps Script.');
        }
      }

      if (success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          type: 'general'
        });
        
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      }

    } catch (error) {
      console.error('❌ Error completo:', error);
      alert('Error al enviar el formulario:\n\n' + error.message + 
            '\n\nPor favor verifica tu conexión e intenta de nuevo.\n' +
            'Si el problema persiste, contáctanos: support@espanolhub.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Shared Header Component */}
      <Header />

      {/* Hero Section - Mismo estilo que Home */}
      <section 
        className="relative py-24 lg:py-32 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/images/spanish.learn.webp)',
          backgroundColor: '#FEF9C3'
        }}
      >
        {/* Background Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-100/95 via-secondary-50/92 to-primary-50/95"></div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-display font-bold text-gray-900 mb-6 leading-tight">
            Contact Us
          </h1>
          <h2 className="text-2xl md:text-4xl font-display font-semibold text-gray-700 mb-8">
            We're here to help you on your Spanish learning journey
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Have questions? Need support? Want to share feedback? Get in touch with our team.
          </p>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-8">
                Send us a Message
              </h2>
              
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <h3 className="text-xl font-display font-semibold text-green-800 mb-2">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-green-700">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                      Inquiry Type
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="partnership">Partnership</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder="Brief description of your inquiry"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder="Please provide details about your inquiry..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition transform hover:scale-105 shadow-lg ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-8">
                Get in Touch
              </h2>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-primary-200 flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-2xl font-bold">@</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-display font-bold text-gray-900 mb-2">Email Support</h3>
                    <p className="text-gray-600 mb-2">
                      Get help with your account, products, or technical issues.
                    </p>
                    <a href="mailto:support@espanolhub.com" className="text-primary-600 hover:text-primary-700 font-medium">
                      support@espanolhub.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-primary-200 flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-2xl font-bold">Chat</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-display font-bold text-gray-900 mb-2">Live Chat</h3>
                    <p className="text-gray-600 mb-2">
                      Chat with our support team in real-time.
                    </p>
                    <p className="text-sm text-gray-500">
                      Available: Monday - Friday, 9 AM - 6 PM EST
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-primary-200 flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-2xl font-bold">Ph</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-display font-bold text-gray-900 mb-2">Phone Support</h3>
                    <p className="text-gray-600 mb-2">
                      Speak directly with our support team.
                    </p>
                    <p className="text-primary-600 font-medium">+1 (555) 123-4567</p>
                    <p className="text-sm text-gray-500">
                      Monday - Friday, 9 AM - 6 PM EST
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-primary-200 flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-2xl font-bold">Wl</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-display font-bold text-gray-900 mb-2">Global Reach</h3>
                    <p className="text-gray-600 mb-2">
                      Serving Spanish learners worldwide.
                    </p>
                    <p className="text-sm text-gray-500">
                      Available in multiple time zones
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ Link */}
              <div className="mt-12 p-6 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg border border-primary-100">
                <h3 className="text-lg font-display font-bold text-gray-900 mb-2">
                  Need Quick Answers?
                </h3>
                <p className="text-gray-600 mb-4">
                  Check our FAQ section for instant answers to common questions.
                </p>
                <Link to="/faq" className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium">
                  Visit FAQ
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Response Time Promise Section - Estilo similar a Home */}
      <section className="py-20" style={{ backgroundColor: '#FF6B6B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              Our Response Time Promise
            </h2>
            <p className="text-lg text-white/90">
              We're committed to getting back to you quickly
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <div className="text-3xl md:text-4xl font-display font-bold text-white mb-2">&lt; 2 hours</div>
              <div className="text-white/90 font-medium">Technical Support</div>
            </div>
            <div className="text-center bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <div className="text-3xl md:text-4xl font-display font-bold text-white mb-2">&lt; 24 hours</div>
              <div className="text-white/90 font-medium">General Inquiries</div>
            </div>
            <div className="text-center bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <div className="text-3xl md:text-4xl font-display font-bold text-white mb-2">&lt; 1 hour</div>
              <div className="text-white/90 font-medium">Billing Issues</div>
            </div>
          </div>
        </div>
      </section>

      {/* Shared Footer Component */}
      <Footer />
    </div>
  );
}

export default ContactPage;
